import { BONUS_ARCHIVES } from "lacinetek-common/src/models/product"
import image, { DIRECTOR_AVATAR } from "lacinetek-common/src/images"
import {
	FEATURE_ACTOR_ID,
	FEATURE_BONUS_ARCHIVE_TITLE,
	FEATURE_BONUS_ARCHIVE_YEAR,
	FEATURE_BONUS_TYPE,
	FEATURE_DIRECTOR_ID,
	metadata
} from "lacinetek-common/src/metadata"

import { useTranslation } from "lib/i18n/translation"
import { useVideoPlayer } from "lib/video-player"
import { FETCH_BONUSES } from "lib/graphql"
import fullname from "lib/tools/name"
import { useMainCategories } from "lib/tools/categories"

import { Link, Products, Query, Slider } from "components/common"
import Image from "components/common/image"
import SkeletonLink from "components/common/skeleton-link"
import Director from "components/common/director"
import Actor from "components/common/actor"

export const SliderRailBonus = ({ bonusIds }) => {
	const query = `id: (${bonusIds.join(" OR ")}) AND mediaSource: (1 OR 4)`

	return (
		<Query
			query={FETCH_BONUSES}
			options={{ variables: { query } }}
		>
			{
				({ cms: { extracts: { items: bonuses } } }) => {
					const orderedBonuses = bonusIds.map(id => bonuses.find(bonus => bonus.id === id))
						.filter(bonus => !!bonus)

					return (
						<Slider
							className="list bonus archives"
							navClassName="slider-controls"
							slides={orderedBonuses}
							slideClassName={"swiper-slide"}
							slideComponent={Bonus}
							asList={false}
						/>
					)
				}
			}
		</Query>
	)
}

const Bonus = ({ loading, index, ...item }) => {
	if (metadata(item, FEATURE_BONUS_TYPE) === BONUS_ARCHIVES) {
		return <BonusArchive bonus={item} />
	}

	return <BonusExclusive bonus={item} />
}

const BonusArchive = ({ bonus }) => {
	const { c } = useTranslation()
	const { seeBonus } = useVideoPlayer()

	const handleSeeBonus = (event, video) => {
		event.preventDefault()
		seeBonus(video, true)
	}

	return (
		<>
			<a
				href="#"
				title={c("see-bonus")}
				className="video-link archives"
				onClick={event => handleSeeBonus(event, bonus)}
			>
				<span className="label">{c("see-bonus")}</span>
				<figure>
					<picture>
						<Image
							src={bonus.cover ?? "/static/images/default_corpus.jpg"}
							alt={`${c("bonus-single")} ${bonus.name}`}
						/>
					</picture>
				</figure>
			</a>
			<div className="quote">
				<div className="source">
					<span>{bonus.name}</span><br />
					{
						metadata(bonus, FEATURE_BONUS_ARCHIVE_TITLE)
						&& <>
							<span className="title">{metadata(bonus, FEATURE_BONUS_ARCHIVE_TITLE)}</span><br />
						</>
					}
					{
						metadata(bonus, FEATURE_BONUS_ARCHIVE_YEAR)
						&& <>
							<span className="source-date">{metadata(bonus, FEATURE_BONUS_ARCHIVE_YEAR)}</span>
						</>
					}
				</div>
			</div>
		</>
	)
}

const BonusExclusive = ({ bonus }) => {
	const { c } = useTranslation()
	const { seeBonus } = useVideoPlayer()
	const { allLists } = useMainCategories()

	const handleSeeBonus = (event, video) => {
		event.preventDefault()
		seeBonus(video, true)
	}

	return (
		<>
			<a
				href="#"
				title={c("see-bonus")}
				className="video-link"
				onClick={event => handleSeeBonus(event, bonus)}
			>
				<span className="label">{c("see-bonus")}</span>
				<figure>
					<picture>
						<Image
							src={bonus.cover}
							alt={`${c("bonus-single")} ${bonus.name}`}
						/>
					</picture>
				</figure>
			</a>
			<Products products={[bonus.product]} loading={false} >
				{(products) => {
					if (products.length === 0) {
						return null
					}

					const [product] = products

					if (metadata(bonus, FEATURE_DIRECTOR_ID)) {
						const list = allLists.find(l => l.director.id === metadata(bonus, FEATURE_DIRECTOR_ID))

						if (list) {
							return <BonusFromListOf product={product} list={list} bonus={bonus} />
						}

						return <Director id={metadata(bonus, FEATURE_DIRECTOR_ID)}>
							{(director) => (
								<BonusFromPersona product={product} persona={director} bonus={bonus} />
							)}
						</Director>
					}

					if (metadata(bonus, FEATURE_ACTOR_ID)) {
						return <Actor id={metadata(bonus, FEATURE_ACTOR_ID)}>
							{(actor) => (
								<BonusFromPersona product={product} persona={actor} bonus={bonus} />
							)}
						</Actor>
					}

					return null
				}}
			</Products>
		</>
	)
}

const BonusFromListOf = ({ product, list, bonus }) => {
	const { c } = useTranslation()

	return (
		<div className="infos">
			<SkeletonLink
				href={`/lists/[id]`}
				as={`/lists/${list.linkRewrite}`}
			>
				<a title={c("see-list")} className="is-recommended">
					<figure className="avatar">
						<picture>
							<Image
								src={image(list.director, DIRECTOR_AVATAR)}
								alt={fullname(list)}
							/>
						</picture>
					</figure>
					<p><span className="list-link">{fullname(list)}</span></p>
				</a>
			</SkeletonLink>
			<BonusProductInfo product={product} bonus={bonus} />
		</div>
	)
}

const BonusFromPersona = ({ product, persona, bonus }) => {
	const { c } = useTranslation()

	return (
		<div className="infos">
			<a title={c("see-list")} className="is-recommended">
				<figure className="avatar">
					<picture>
						<Image
							src={image(persona, DIRECTOR_AVATAR)}
							alt={persona.name}
						/>
					</picture>
				</figure>
				<p><span className="list-link">{persona.name}</span></p>
			</a>
			<BonusProductInfo product={product} bonus={bonus} />
		</div>
	)
}

const BonusProductInfo = ({ product, bonus }) => {
	const { c } = useTranslation()

	return (
		<>
			<p className="is-about">
				{c("about-of")}
				{" "}
				<Link
					target={`/film/[id]`}
					as={`/film/${product.linkRewrite}`}
					title={product.name}
					className="title"
				/>
				<span className="director"> {c("of")} {product.director}</span>
			</p>
			{
				bonus.description
				&& <div className="quote">
					<blockquote>{bonus.description}</blockquote>
				</div>
			}
		</>
	)
}
